import { forwardRef, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  AtSign, ChevronDown, LayoutGrid, Lightbulb, Loader2, Send, Square, Wand2, X,
} from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { ProjectOption, ProjectSearchList, filterProjects } from './ProjectPicker';
import { useMediaQuery } from './ui';

/** "Create a task for [project name]: …" with the [parts to replace] tinted. */
export function PromptText({ text }) {
  return String(text).split(/(\[[^\]]+\])/).map((part, index) => (/^\[.*\]$/.test(part) ? (
    // eslint-disable-next-line react/no-array-index-key
    <span key={index} className="rounded bg-[color:var(--status-gray-bg)] px-1 text-ink-3">{part.slice(1, -1)}</span>
  ) : part));
}

const chipClass = 'inline-flex h-[28px] max-w-full shrink-0 items-center gap-1.5 rounded-full border border-line bg-surface px-2.5 text-[12.5px] font-medium text-ink-2 transition-colors hover:border-[color:var(--brand-soft-border)] hover:bg-brand-soft hover:text-brand-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

/** The grouped prompt library (popover list or cards). Clicking a prompt only fills the box. */
export function PromptLibrary({ library, onPick, variant = 'list' }) {
  if (variant === 'cards') {
    return (
      <div className="grid gap-3 sm:grid-cols-2" data-testid="ai-prompt-cards">
        {library.map((group) => (
          <div key={group.key} className="rounded-xl border border-line bg-surface p-3">
            <p className="px-1 pb-1.5 text-[11px] font-bold uppercase tracking-[0.06em] text-ink-3">{group.label}</p>
            <ul className="space-y-0.5">
              {group.prompts.map((prompt) => (
                <li key={prompt.text}>
                  <button
                    type="button"
                    onClick={() => onPick(prompt.text)}
                    className="flex w-full items-start gap-2 rounded-lg px-2 py-1.5 text-left text-[13px] text-ink-2 hover:bg-surface-hover hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    {prompt.action ? <Wand2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-status-green-ink" aria-label="Makes a change" /> : null}
                    <span><PromptText text={prompt.text} /></span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    );
  }
  return (
    <div className="max-h-[min(420px,60vh)] overflow-y-auto p-1.5">
      {library.map((group) => (
        <div key={group.key} className="pb-1">
          <p className="px-2 pb-1 pt-2 text-[11px] font-bold uppercase tracking-[0.06em] text-ink-3">
            {group.label} <span className="font-semibold tabular-nums">{group.prompts.length}</span>
          </p>
          {group.prompts.map((prompt) => (
            <button
              key={prompt.text}
              type="button"
              onClick={() => onPick(prompt.text)}
              className="flex w-full items-start gap-2 rounded-lg px-2 py-[7px] text-left text-[13px] text-ink hover:bg-surface-hover focus-visible:bg-surface-hover focus-visible:outline-none"
            >
              {prompt.action ? <Wand2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-status-green-ink" aria-hidden="true" /> : <span className="w-3.5 shrink-0" />}
              <span><PromptText text={prompt.text} /></span>
            </button>
          ))}
        </div>
      ))}
      <p className="border-t border-line-2 px-2 pb-1 pt-2 text-[11.5px] text-ink-3">
        <Wand2 className="mr-1 inline h-3 w-3 text-status-green-ink" aria-hidden="true" />
        makes a change — it asks you to confirm first, then gives a card with Undo.
      </p>
    </div>
  );
}

/**
 * Quick prompts on a wide screen: only the chips that fit whole are shown — never one cut mid-word; the rest
 * are in "All prompts". Every chip stays rendered (later ones hidden), so widths are measured without a re-layout
 * loop. Returns the number of chips that fit, or null while not measuring (phone: the row scrolls sideways).
 */
function useChipsThatFit(rowRef, enabled, count) {
  const [fit, setFit] = useState(null);
  useLayoutEffect(() => {
    const row = rowRef.current;
    if (!enabled || !row) {
      setFit(null);
      return undefined;
    }
    const measure = () => {
      const limit = row.clientWidth;
      const chips = Array.from(row.querySelectorAll('[data-quick-prompt]'));
      let shown = 0;
      for (const chip of chips) {
        if (chip.offsetLeft + chip.offsetWidth > limit + 0.5) break;
        shown += 1;
      }
      setFit((current) => (current === shown ? current : shown));
    };
    measure();
    const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(measure) : null;
    observer?.observe(row);
    window.addEventListener('resize', measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [rowRef, enabled, count]);
  return fit;
}

/** "@liv wat" right before the caret (at the start or after a space) → { query, start }; else null. */
export function mentionAt(text, caret) {
  const before = String(text || '').slice(0, caret ?? String(text || '').length);
  const match = /(^|\s)@([^@\n]{0,40})$/.exec(before);
  if (!match) return null;
  return { query: match[2], start: before.length - match[2].length - 1 };
}

const scopeChipClass = 'inline-flex h-[26px] max-w-[240px] shrink-0 items-center gap-1 rounded-full border px-2 text-[12px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

/** "All projects ⌄" / "@ LIV Waterside ×" — which project the answers are about (25 idea 7). */
function ScopeChip({ scope, onScopeChange, projectList, onWantProjects, disabled }) {
  const [open, setOpen] = useState(false);
  if (scope) {
    return (
      <span className={cn(scopeChipClass, 'border-[color:var(--brand-soft-border)] bg-brand-soft pr-1 text-brand-ink')} data-testid="ai-scope-chip">
        <AtSign className="h-3 w-3 shrink-0" aria-hidden="true" />
        <span className="truncate" title={`Answers are about ${scope.name}`}>{scope.name}</span>
        <button
          type="button"
          onClick={() => onScopeChange(null)}
          disabled={disabled}
          aria-label={`Ask about all projects again (remove ${scope.name})`}
          className="grid h-5 w-5 shrink-0 place-items-center rounded-full text-brand-ink hover:bg-surface"
          data-testid="ai-scope-clear"
        >
          <X className="h-3 w-3" aria-hidden="true" />
        </button>
      </span>
    );
  }
  return (
    <Popover open={open} onOpenChange={(next) => { setOpen(next); if (next) onWantProjects?.(); }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(scopeChipClass, 'border-line bg-surface text-ink-2 hover:bg-surface-hover data-[state=open]:bg-surface-hover')}
          data-testid="ai-scope-chip"
        >
          <AtSign className="h-3 w-3 shrink-0" aria-hidden="true" />
          All projects
          <ChevronDown className="h-3 w-3 text-ink-3" aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" side="top" className="w-[300px] rounded-xl border-line bg-surface p-0 shadow-overlay">
        <p className="border-b border-line-2 px-3 py-2 text-[12px] text-ink-3">
          Focus the answers on one project — or type <kbd className="ui-kbd">@</kbd> in the box.
        </p>
        <ProjectSearchList
          projects={projectList?.projects}
          loading={projectList?.loading}
          error={projectList?.error}
          onRetry={projectList?.retry}
          onPick={(project) => { onScopeChange(project); setOpen(false); }}
          testId="ai-scope-list"
        />
      </PopoverContent>
    </Popover>
  );
}

/**
 * Composer: quick prompt chips + "All prompts", the question box with the project scope ("All projects"
 * or "@LIV Waterside", also by typing @), ↵ send · ⇧↵ new line, and Stop while an answer is being written
 * (button or Esc).
 */
const AiComposer = forwardRef(function AiComposer({
  value, onChange, onSend, onStop, sending, disabled, library, quickPrompts, showChips = true, onPick, placeholder, note,
  scope, onScopeChange, projectList, onWantProjects,
}, ref) {
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [mention, setMention] = useState(null);
  const [active, setActive] = useState(0);
  const localRef = useRef(null);
  const inputRef = ref || localRef;
  const chipRowRef = useRef(null);
  const wide = useMediaQuery('(min-width: 1024px)');
  const chipsFit = useChipsThatFit(chipRowRef, wide && showChips, quickPrompts?.length || 0);
  const matches = useMemo(
    () => (mention ? filterProjects(projectList?.projects, mention.query, 6) : []),
    [mention, projectList?.projects]
  );

  useEffect(() => { setActive(0); }, [mention?.query]);

  // After a project is picked the text changes; put the caret back right after React wrote the new value,
  // before the next key arrives (a timeout would let fast typing land in the wrong place).
  const caretRef = useRef(null);
  useLayoutEffect(() => {
    if (caretRef.current === null || !inputRef.current) return;
    inputRef.current.focus();
    inputRef.current.setSelectionRange(caretRef.current, caretRef.current);
    caretRef.current = null;
  });

  const pick = (text) => {
    setLibraryOpen(false);
    onPick(text);
  };

  const readMention = (text, caret) => {
    const next = onScopeChange ? mentionAt(text, caret) : null;
    setMention(next);
    if (next) onWantProjects?.();
  };

  const chooseProject = (project) => {
    if (!mention) return;
    const node = inputRef.current;
    const caret = node?.selectionStart ?? value.length;
    const before = value.slice(0, mention.start).replace(/\s+$/, '');
    const after = value.slice(caret).replace(/^\s+/, '');
    const nextText = [before, after].filter(Boolean).join(' ');
    caretRef.current = before.length + (before && after ? 1 : 0);
    onScopeChange(project);
    onChange(nextText);
    setMention(null);
  };

  const onKeyDown = (event) => {
    if (mention && matches.length) {
      if (event.key === 'ArrowDown') { event.preventDefault(); setActive((index) => Math.min(index + 1, matches.length - 1)); return; }
      if (event.key === 'ArrowUp') { event.preventDefault(); setActive((index) => Math.max(index - 1, 0)); return; }
      if ((event.key === 'Enter' || event.key === 'Tab') && matches[active]) { event.preventDefault(); chooseProject(matches[active]); return; }
    }
    if (event.key === 'Escape') {
      if (mention) { event.preventDefault(); setMention(null); return; }
      if (sending && onStop) { event.preventDefault(); onStop(); return; }
    }
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent?.isComposing) {
      event.preventDefault();
      onSend();
    }
  };

  const canSend = Boolean(value.trim()) && !sending && !disabled;
  const showMentions = Boolean(mention) && (matches.length > 0 || projectList?.loading || Boolean(projectList?.projects));

  return (
    <div className="shrink-0 border-t border-line px-4 pb-4 pt-3 sm:px-5" data-testid="ai-composer">
      <div className="mb-2.5 flex min-w-0 items-center gap-2">
        {showChips ? (
          <div
            ref={chipRowRef}
            className={cn(
              'relative flex min-w-0 flex-1 items-center gap-2 pb-0.5',
              wide ? 'overflow-hidden' : 'overflow-x-auto pr-6 [mask-image:linear-gradient(to_right,black_calc(100%-28px),transparent)]'
            )}
            data-testid="ai-quick-prompts"
          >
            <span className={cn('inline-flex shrink-0 items-center gap-1 text-[12px] font-semibold text-ink-3', chipsFit === 0 && 'invisible')}>
              <Lightbulb className="h-3.5 w-3.5 text-status-amber" aria-hidden="true" />
              Try
            </span>
            {quickPrompts.map((prompt, index) => {
              const hidden = chipsFit !== null && index >= chipsFit;
              return (
                <button
                  key={prompt}
                  type="button"
                  data-quick-prompt=""
                  className={cn(chipClass, wide && 'max-w-none', hidden && 'invisible')}
                  onClick={() => pick(prompt)}
                  aria-hidden={hidden || undefined}
                  tabIndex={hidden ? -1 : undefined}
                >
                  <span className="truncate">{prompt}</span>
                </button>
              );
            })}
          </div>
        ) : <div className="flex-1" />}
        <Popover open={libraryOpen} onOpenChange={setLibraryOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              data-testid="ai-all-prompts"
              className="inline-flex h-[28px] shrink-0 items-center gap-1.5 rounded-lg px-2 text-[12.5px] font-semibold text-ink-2 hover:bg-surface-hover hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring data-[state=open]:bg-surface-hover"
            >
              <LayoutGrid className="h-3.5 w-3.5" aria-hidden="true" />
              All prompts
              <ChevronDown className="h-3 w-3 text-ink-3" aria-hidden="true" />
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" side="top" className="w-[min(420px,calc(100vw-2rem))] rounded-xl border-line bg-surface p-0 shadow-overlay">
            <PromptLibrary library={library} onPick={pick} />
          </PopoverContent>
        </Popover>
      </div>

      <div className="relative">
        {showMentions ? (
          <div
            className="absolute bottom-full left-0 z-20 mb-1.5 w-[min(340px,100%)] rounded-xl border border-line bg-surface p-1.5 shadow-overlay"
            data-testid="ai-mention-list"
          >
            <p className="px-2 pb-1 pt-0.5 text-[11px] font-bold uppercase tracking-[0.06em] text-ink-3">Focus on a project</p>
            {projectList?.loading ? <p className="px-2.5 py-1.5 text-[12.5px] text-ink-3">Loading projects…</p> : null}
            {!projectList?.loading && !matches.length ? (
              <p className="px-2.5 py-1.5 text-[12.5px] text-ink-3">No project matches “{mention.query}”.</p>
            ) : null}
            <div role="listbox" aria-label="Projects">
              {matches.map((project, index) => (
                <ProjectOption key={project.id} project={project} active={index === active} onSelect={chooseProject} id={`ai-mention-${index}`} />
              ))}
            </div>
          </div>
        ) : null}
        <div className={cn(
          'rounded-[14px] border border-line-2 bg-surface px-3.5 pb-2.5 pt-3 transition-shadow focus-within:border-[color:var(--brand-ink)] focus-within:ring-2 focus-within:ring-[color:var(--brand-soft-border)]',
          disabled && 'opacity-60'
        )}>
          <textarea
            ref={inputRef}
            value={value}
            onChange={(event) => { onChange(event.target.value); readMention(event.target.value, event.target.selectionStart); }}
            onKeyDown={onKeyDown}
            onClick={(event) => readMention(event.target.value, event.target.selectionStart)}
            onBlur={() => window.setTimeout(() => setMention(null), 150)}
            rows={2}
            disabled={disabled}
            placeholder={placeholder}
            aria-label="Question"
            aria-autocomplete={onScopeChange ? 'list' : undefined}
            aria-activedescendant={showMentions && matches[active] ? `ai-mention-${active}` : undefined}
            data-testid="ai-input"
            className="block max-h-[200px] min-h-[44px] w-full resize-none bg-transparent text-[14px] leading-[1.5] text-ink outline-none placeholder:text-ink-4"
          />
          <div className="mt-1.5 flex items-center gap-3">
            {onScopeChange ? (
              <ScopeChip scope={scope} onScopeChange={onScopeChange} projectList={projectList} onWantProjects={onWantProjects} disabled={sending} />
            ) : null}
            <span className="ml-auto hidden items-center gap-1 text-[11.5px] text-ink-3 sm:inline-flex" aria-live="polite">
              {sending ? (
                <>Answering… <kbd className="ui-kbd">Esc</kbd> to stop</>
              ) : (
                <><kbd className="ui-kbd">↵</kbd> send · <kbd className="ui-kbd">⇧</kbd><kbd className="ui-kbd">↵</kbd> new line</>
              )}
            </span>
            {sending && onStop ? (
              <button
                type="button"
                onClick={onStop}
                aria-label="Stop answering"
                data-testid="ai-stop"
                className="ml-auto inline-flex h-9 shrink-0 items-center gap-1.5 rounded-[10px] border border-line-2 bg-surface px-3 text-[13px] font-semibold text-ink hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:ml-0"
              >
                <Square className="h-3 w-3 fill-current" aria-hidden="true" />
                Stop
              </button>
            ) : (
              <button
                type="button"
                onClick={onSend}
                disabled={!canSend}
                aria-label="Send"
                data-testid="ai-send"
                className="ml-auto grid h-9 w-9 shrink-0 place-items-center rounded-[10px] bg-brand text-brand-fg shadow-[0_1px_2px_rgb(25_25_112/0.25)] transition-colors hover:bg-brand-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-[color:var(--status-gray-bg)] disabled:text-ink-4 disabled:shadow-none sm:ml-0"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Send className="h-4 w-4" aria-hidden="true" />}
              </button>
            )}
          </div>
        </div>
      </div>
      {note ? <p className="mt-2 text-center text-[11.5px] text-ink-3">{note}</p> : null}
    </div>
  );
});

export default AiComposer;
